import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import PLAN_CONFIG from '../settings/planConfig';
import { IOS_IAP_PRODUCT_IDS } from '../settings/config';
import {
  isIOSIAPAvailable,
  loadIOSProducts,
  purchaseIOSProduct,
  restoreIOSPurchases,
} from '../services/iosIap';

const IOSIAPPurchasePage = ({ t, planData, userPlan, userToken, onPlanChanged }) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [storeProducts, setStoreProducts] = useState({});
  const [processingId, setProcessingId] = useState('');
  const [message, setMessage] = useState(null);
  const bridgeAvailable = isIOSIAPAvailable();

  const plans = useMemo(
    () => (planData || PLAN_CONFIG).filter((plan) => (
      !String(plan.id).startsWith('manual') && plan.id !== 'free'
    )),
    [planData]
  );

  useEffect(() => {
    if (!bridgeAvailable) return;

    const productIds = Object.values(IOS_IAP_PRODUCT_IDS);
    loadIOSProducts(productIds)
      .then((result) => {
        const products = result.products || [];
        setStoreProducts(Object.fromEntries(products.map((product) => [product.productId, product])));
      })
      .catch((error) => setMessage({ severity: 'error', text: error.message }));
  }, [bridgeAvailable]);

  const handlePurchase = async (planId) => {
    const productId = IOS_IAP_PRODUCT_IDS[planId];
    if (!productId || processingId) return;

    setProcessingId(planId);
    setMessage(null);
    try {
      await purchaseIOSProduct({ productId, planId, userToken });
      setMessage({
        severity: 'success',
        text: t?.sb_iap_purchase_success || 'Your App Store purchase was completed successfully.',
      });
      await onPlanChanged?.();
    } catch (error) {
      setMessage({ severity: 'error', text: error.message });
    } finally {
      setProcessingId('');
    }
  };

  const handleRestore = async () => {
    if (processingId) return;

    setProcessingId('restore');
    setMessage(null);
    try {
      await restoreIOSPurchases(userToken);
      setMessage({
        severity: 'success',
        text: t?.sb_iap_restore_success || 'Your App Store purchases have been restored.',
      });
      await onPlanChanged?.();
    } catch (error) {
      setMessage({ severity: 'error', text: error.message });
    } finally {
      setProcessingId('');
    }
  };

  return (
    <>
      <Typography
        variant="subtitle1"
        sx={{
          mt: 1,
          mb: 1,
          color: 'text.primary',
          fontWeight: 600,
          fontSize: { xs: 22, sm: 26 },
          lineHeight: 1.4,
          textAlign: 'center',
        }}
      >
        {t?.sb_purchase_plans || 'Choose a Plan'}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} align="center">
        {t?.sb_iap_billed_by_apple || 'Subscriptions are billed securely through your Apple ID.'}
      </Typography>

      {!bridgeAvailable && (
        <Alert severity="warning" sx={{ maxWidth: 700, mx: 'auto', mb: 3 }}>
          {t?.sb_iap_unavailable || 'App Store purchases are only available inside the iOS app.'}
        </Alert>
      )}
      {message && (
        <Alert severity={message.severity} sx={{ maxWidth: 700, mx: 'auto', mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
        <Typography color={isAnnual ? 'text.secondary' : 'text.primary'}>
          {t?.sb_monthly || 'Monthly'}
        </Typography>
        <Switch checked={isAnnual} onChange={(event) => setIsAnnual(event.target.checked)} />
        <Typography color={isAnnual ? 'text.primary' : 'text.secondary'}>
          {t?.sb_annual || 'Annual'}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(240px, 1fr))' }, gap: 3, maxWidth: 760, mx: 'auto' }}>
        {plans.map((plan) => {
          const details = plan.monthly && plan.annual
            ? (isAnnual ? plan.annual : plan.monthly)
            : plan;
          const planId = String(details.id);
          const productId = IOS_IAP_PRODUCT_IDS[planId];
          const storeProduct = storeProducts[productId];
          const isCurrent = String(userPlan?.plan_id) === planId;
          const features = (details.features || []).map((key) => t?.[key] || key);

          return (
            <Box key={planId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, boxShadow: 2, p: 3, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" fontWeight={700} align="center">
                {t?.[plan.nameKey] || plan.name || plan.id}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600} align="center" mt={1}>
                {storeProduct?.displayPrice || storeProduct?.price || details.price}
              </Typography>
              <Box component="ul" sx={{ flexGrow: 1, pl: 2 }}>
                {features.map((feature) => <li key={feature}>{feature}</li>)}
              </Box>
              <Button
                variant={isCurrent ? 'contained' : 'outlined'}
                color="inherit"
                disabled={isCurrent || Boolean(processingId) || !bridgeAvailable || !productId}
                onClick={() => handlePurchase(planId)}
              >
                {processingId === planId
                  ? <CircularProgress size={22} color="inherit" />
                  : isCurrent
                    ? (t?.sb_current_plan || 'Current Plan')
                    : (t?.sb_iap_subscribe || 'Subscribe with Apple')}
              </Button>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 4 }}>
        <Button variant="text" color="inherit" onClick={handleRestore} disabled={!bridgeAvailable || Boolean(processingId)}>
          {processingId === 'restore'
            ? (t?.sb_processing || 'Processing...')
            : (t?.sb_iap_restore || 'Restore Purchases')}
        </Button>
        <Link href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener noreferrer">
          {t?.sb_manage_apple_subscriptions || 'Manage App Store subscriptions'}
        </Link>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ maxWidth: 620 }}>
          {t?.sb_iap_terms || 'Payment will be charged to your Apple ID. Subscriptions renew automatically unless cancelled through your App Store account.'}
        </Typography>
      </Box>
    </>
  );
};

export default IOSIAPPurchasePage;
